import { getMobileOS } from "@/shared/utils/os_logic";
import {
  ErrorDialogContext,
  errorHandledAsync,
} from "@/shared/context/error-context";
import SelectConfirmDialog from "@/shared/components/photo-shoot-components/select-confirm-dialog";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import React, { useContext, useEffect, useState } from "react";
import { OcrCameraAggregateMode } from "@/shared/config/globals"
import { OcrCameraAggregateModeType } from "@/shared/config/globals";
import { OcrCameraNextAction } from "@/shared/config/globals";

interface ConfirmViewOption {
  changeStatus: (mode: OcrCameraAggregateModeType) => void;
  imageList: string[];
  addPhotoShootCount: () => void;
  photoShootCount: number;
  resetImageList: () => void;
  deleteImageUrl: (num: number) => void;
  resetPhotoShootCount: () => void;
  handleAggregate: () => void;
}

export const ConfirmView = (props: ConfirmViewOption) => {
  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);

  // 撮影を続けるか、作成するかの確認ダイアログ表示フラグ
  const [dialogVisible, setDialogVisible] = useState(false);

  // ダイアログ外タップ時
  const dialogOutAreaOnTap = () => {
    setDialogVisible(false);
  };

  // 撮り直すボタンクリック時
  const onBackButtonClick = () => {
    props.changeStatus(OcrCameraAggregateMode.Shoot);
    props.deleteImageUrl(props.photoShootCount);
  };

  // 次へボタンクリック時
  const onNextButtonClick = () => {
    setDialogVisible(true);
  };

  // モーダルサイズ取得
  const getModalDimensions = () => {
    const el = document.getElementsByClassName("md-add-excel")[0] as HTMLElement | undefined;
    if (!el) {
      return { width: 0, height: 0 };
    }
    return {
      width: el.clientWidth,
      height: el.clientHeight,
    };
  };

  // 画面の向き取得
  const getOrientation = () => {
    let orientation = "unknown";
    if (window.matchMedia("(orientation: portrait)").matches) {
      orientation = "portrait";
    }
    if (window.matchMedia("(orientation: landscape)").matches) {
      orientation = "landscape";
    }
    return orientation;
  };

  // カメラ領域の計算（現状全て適当な値）
  const getCameraSize = () => {
    const windowSize = getModalDimensions();
    const isPortrait = getOrientation() === "portrait";
    const os = getMobileOS();
    if (os === "iPhone" || os === "Android") {
      // Note: INVBG-152にてuserAgentを修正したが、カメラ領域に関してはコードを精査してから修正する必要があるため、今までと同様の状態になるようにiPadはこの分岐に含めていない
      if (isPortrait) {
        return {
          width: windowSize.width * (85 / 100.0),
          height: windowSize.height * (65 / 100.0),
        };
      } else {
        return {
          width: windowSize.width * (65 / 100.0),
          height: windowSize.height * (85 / 100.0),
        };
      }
    }
    return {
      width: windowSize.width * (85 / 100.0),
      height: windowSize.height * (65 / 100.0),
    };
  };

  // 現在のカメラ領域
  const [cameraSize, setCameraSize] = useState(getCameraSize());

  // 撮影画像描画
  const renderImage = (): JSX.Element => {
    // 画像が存在するかチェック
    if (props.imageList && props.imageList[props.photoShootCount] !== null) {
      return (
        <img
          src={props.imageList[props.photoShootCount]}
          className={"photographed-image"}
          alt="Screenshot"
        />
      );
    } else {
      return <></>;
    }
  };

  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);

  /**
   * ダイアログボタン押下時の処理
   * @param result  Const.CONFIRM_RESUME or Const.CONFIRM_FINISH
   */
  const onDecideType = async (result: string) => {
    if (result === OcrCameraNextAction.Resume) {  // 追加撮影
      // 撮影した写真を登録
      props.addPhotoShootCount();
      props.changeStatus(OcrCameraAggregateMode.Shoot);
    } else {  // 作成
      try {
        submitImage();
      } catch (error) {
        // NOTE: エラー後続処理が必要な場合は、callback を適宜指定すること
        // throw KBException.wrapCallback(error, () => {});
        console.error(error);
      }
    }
  };

  // NOTE: コントローラ内部でのみ使用される関数のため、使用される前にグローバルエラーハンドリングを行う
  const wrapperForErrorHandling = () => {
    // 撮影画像登録処理
    const submitImage = async () => {
      try {
        loadingCtx.setIsLoading(true);
        props.changeStatus(OcrCameraAggregateMode.Shoot);
        props.resetImageList();
        props.resetPhotoShootCount();
        props.handleAggregate();
      } catch (error) {
        // NOTE: エラー後続処理が必要な場合は、callback を適宜指定すること
        // throw KBException.wrapCallback(error, () => {});
        console.error(error);
      } finally {
        loadingCtx.setIsLoading(false);
      }
    };

    return { submitImage: errorHandledAsync(submitImage, setError) };
  };
  const { submitImage } = wrapperForErrorHandling();

  // ウィンドウリサイズ時処理
  const handleWindowResize = () => {
    setCameraSize(getCameraSize());
  };

  // 描画完了時
  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  return (
    <>
      {/* キャプチャ画像 */}
      <div className="photograph-image">
        <div
          style={{
            width: cameraSize.width + "px",
            height: cameraSize.height + "px",
          }}
        >
          {renderImage()}
        </div>
      </div>

      {/* ボタン領域 */}
      <div className="confirm-photo-button">
        <button
          className="btn-base"
          onClick={onBackButtonClick}
        >
          撮り直す
        </button>
        <button
          className="nav-main"
          onClick={onNextButtonClick}
        >
          次へ
        </button>
      </div>

      {dialogVisible && (
        <div className="confirm-photo-dialog">
          {/*  確認ダイアログ */}
          <SelectConfirmDialog
            isShow={dialogVisible}
            title="撮影した画像を取り込みますか？"
            leftAreaOnTap={() => {
              props.photoShootCount + 1 < 5
              ? onDecideType(OcrCameraNextAction.Resume)
              : setDialogVisible(false);
            }}
            leftAreaText={props.photoShootCount + 1 < 5 ? "追加で撮影" : "キャンセル"}
            rightAreaOnTap={() => onDecideType(OcrCameraNextAction.Finish)}
            rightAreaText={"取り込む"}
            outAreaOnTap={dialogOutAreaOnTap}
          />
        </div>
      )}
    </>
  );
};
